import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SettingsSubPageWrapper } from '../Settings';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Link2,
  Key,
  RefreshCw,
  ExternalLink,
  Check,
  Trash2,
  Eye,
  EyeOff,
  Database,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoyverseSettings() {
  const [keyInfo, setKeyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);

  useEffect(() => { fetchKeyInfo(); }, []);

  const fetchKeyInfo = async () => {
    try {
      const res = await axios.get(`${API}/settings/loyverse-key`, { withCredentials: true });
      setKeyInfo(res.data);
    } catch {
      setKeyInfo({ has_key: false });
    } finally { setLoading(false); }
  };

  const handleSaveKey = async () => {
    if (!newKey.trim()) { toast.error('Please enter an API key'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/settings/loyverse-key`, { api_key: newKey.trim() }, { withCredentials: true });
      toast.success('Loyverse API key saved and verified');
      setNewKey('');
      setShowKeyInput(false);
      fetchKeyInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save API key');
    } finally { setSaving(false); }
  };

  const handleDeleteKey = async () => {
    if (!window.confirm('Remove the Loyverse API key from the database?')) return;
    try {
      await axios.delete(`${API}/settings/loyverse-key`, { withCredentials: true });
      toast.success('API key removed');
      fetchKeyInfo();
    } catch { toast.error('Failed to remove API key'); }
  };

  if (loading) {
    return (
      <SettingsSubPageWrapper title="Loyverse Integration">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </SettingsSubPageWrapper>
    );
  }

  return (
    <SettingsSubPageWrapper title="Loyverse Integration">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Loyverse Integration
        </h1>
        <p className="text-muted-foreground mt-1">Manage your Loyverse POS connection</p>
      </div>

      {/* Connection Status */}
      <Card data-testid="loyverse-status-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Database className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <p className="font-medium">Loyverse POS</p>
                <p className="text-sm text-muted-foreground">
                  {keyInfo?.has_key ? `Key: ${keyInfo.masked_key}` : 'No API key configured'}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className={keyInfo?.has_key ? 'text-green-600 border-green-600' : 'text-red-500 border-red-500'}
            >
              {keyInfo?.has_key ? 'Connected' : 'Not Connected'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* API Key Management */}
      <Card data-testid="loyverse-key-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            API Key
          </CardTitle>
          <CardDescription>
            Your Loyverse API key is used to sync products, branches, and push recipe costs.
            Get your key from{' '}
            <a href="https://r.loyverse.com/dashboard/#/api" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Loyverse Dashboard &rarr; API Access
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showKeyInput ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Loyverse API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showKey ? 'text' : 'password'}
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      placeholder="Paste your Loyverse API key here"
                      data-testid="loyverse-key-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowKey(!showKey)}
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveKey} disabled={saving} data-testid="save-loyverse-key-btn">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  {saving ? 'Verifying...' : 'Save & Verify'}
                </Button>
                <Button variant="outline" onClick={() => { setShowKeyInput(false); setNewKey(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button onClick={() => setShowKeyInput(true)} data-testid="change-loyverse-key-btn">
                <Key className="w-4 h-4 mr-2" />
                {keyInfo?.has_key ? 'Change API Key' : 'Add API Key'}
              </Button>
              {keyInfo?.has_key && (
                <Button variant="outline" className="text-destructive" onClick={handleDeleteKey} data-testid="delete-loyverse-key-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Key
                </Button>
              )}
              <Button variant="outline" asChild>
                <a href="https://r.loyverse.com/dashboard/#/api" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Get API Key
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Re-sync & Match Recipes */}
      {keyInfo?.has_key && (
        <Card data-testid="resync-match-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Re-sync & Match Recipes
            </CardTitle>
            <CardDescription>
              Sync the latest products from Loyverse, then attempt to match any unlinked recipes from the Master Stock List import.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={async () => {
                setResyncing(true);
                setResyncResult(null);
                try {
                  const res = await axios.post(`${API}/import/resync-and-match`, {}, { withCredentials: true, timeout: 120000 });
                  setResyncResult(res.data);
                  toast.success(`Synced ${res.data.loyverse_sync?.new || 0} new products, matched ${res.data.recipe_matching?.new_recipes || 0} new recipes`);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Re-sync failed');
                } finally { setResyncing(false); }
              }}
              disabled={resyncing}
              data-testid="resync-match-btn"
            >
              {resyncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              {resyncing ? 'Syncing & Matching...' : 'Run Re-sync & Match'}
            </Button>
            {resyncResult && (
              <div className="p-4 bg-muted rounded-lg text-sm space-y-2">
                <p className="font-medium">Results:</p>
                <div className="grid grid-cols-2 gap-2">
                  <p>New Loyverse products: <strong>{resyncResult.loyverse_sync?.new || 0}</strong></p>
                  <p>Updated products: <strong>{resyncResult.loyverse_sync?.updated || 0}</strong></p>
                  <p>New recipes matched: <strong>{resyncResult.recipe_matching?.new_recipes || 0}</strong></p>
                  <p>Already matched: <strong>{resyncResult.recipe_matching?.already_matched || 0}</strong></p>
                  <p>Still unmatched: <strong>{resyncResult.recipe_matching?.still_unmatched || 0}</strong></p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* Sync Info */}
      <Card>
        <CardHeader>
          <CardTitle>What gets synced</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <p><strong>Menu Products</strong> — All Loyverse items are synced as menu products. Create recipes to track costs.</p>
            </div>
            <div className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <p><strong>Branches</strong> — Loyverse stores can be synced as branches in Settings &gt; Branches.</p>
            </div>
            <div className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <p><strong>Cost Push</strong> — Recipe costs can be pushed back to update item cost prices in Loyverse.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </SettingsSubPageWrapper>
  );
}
